import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Circle, Download, Paperclip, Square } from "lucide-react";
import { buildExecutionTraceViewModel } from "../../utils/executionTrace";

const statusLabels = { running: "Running", completed: "Completed", failed: "Failed", blocked: "Blocked", stopped: "Stopped" };
const formatDuration = (ms) => {
  const value = Number(ms);
  if (!Number.isFinite(value) || value <= 0) return "";
  return value < 1000 ? `${Math.round(value)}ms` : `${(value / 1000).toFixed(value < 10000 ? 1 : 0)}s`;
};
const formatFileSize = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value < 1024) return `${Math.round(value)} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const StatusIcon = React.memo(function StatusIcon({ status }) {
  if (status === "completed") return <Check aria-label="Completed" />;
  if (status === "failed" || status === "blocked") return <AlertTriangle aria-label={status === "blocked" ? "Blocked" : "Failed"} />;
  if (status === "stopped") return <Square aria-label="Stopped" />;
  return <Circle className="execution-trace-running-dot" aria-label="Running" />;
});

const Artifact = React.memo(function Artifact({ artifact }) {
  const details = [artifact.fileType, artifact.slideCount ? `${artifact.slideCount} slide${artifact.slideCount === 1 ? "" : "s"}` : "", formatFileSize(artifact.fileSize)].filter(Boolean);
  return (
    <div className="execution-trace-artifact">
      <Paperclip aria-hidden="true" />
      <span className="execution-trace-artifact-copy">
        <span className="execution-trace-artifact-name">{artifact.name}</span>
        {details.length ? <span className="execution-trace-artifact-meta">{details.join(" · ")}</span> : null}
      </span>
      {artifact.url ? <a className="execution-trace-artifact-action" href={artifact.url} target="_blank" rel="noreferrer" aria-label={`Open ${artifact.name}`}><Download aria-hidden="true" /></a> : null}
    </div>
  );
});

const Step = React.memo(function Step({ step, depth = 0 }) {
  const detail = [step.summary, formatDuration(step.duration)].filter(Boolean).join(" · ");
  return (
    <div className="execution-trace-step-group">
      <div className={`execution-trace-step execution-trace-step--${step.status}`} style={{ "--execution-trace-depth": Math.min(depth, 4) }} data-depth={depth}>
        <span className="execution-trace-icon"><StatusIcon status={step.status} /></span>
        <span className="execution-trace-step-copy">
          <span className="execution-trace-step-title">{step.title}</span>
          {detail ? <span className="execution-trace-step-detail">{detail}</span> : null}
          {step.artifacts.map((artifact) => <Artifact key={artifact.id} artifact={artifact} />)}
        </span>
      </div>
      {step.children.map((child) => <Step key={child.id} step={child} depth={depth + 1} />)}
    </div>
  );
});

const ExecutionTraceView = ({ trace, steps, status, isStreaming = false, defaultCollapsed = true, summary = "", sourcesCount = 0, duration = null }) => {
  const manuallyToggled = useRef(false);
  const [collapsed, setCollapsed] = useState(!isStreaming && defaultCollapsed);
  const viewModel = useMemo(() => buildExecutionTraceViewModel(
    trace && typeof trace === "object" ? trace : { steps, status, summary, sourcesCount, durationMs: duration },
  ), [duration, sourcesCount, status, steps, summary, trace]);

  useEffect(() => {
    if (!manuallyToggled.current) setCollapsed(!isStreaming && defaultCollapsed);
  }, [defaultCollapsed, isStreaming, viewModel.status]);

  if (!viewModel.stepCount && !viewModel.artifactCount) return null;
  const metadata = [
    `${viewModel.stepCount} step${viewModel.stepCount === 1 ? "" : "s"}`,
    viewModel.artifactCount ? `${viewModel.artifactCount} artifact${viewModel.artifactCount === 1 ? "" : "s"}` : "",
    formatDuration(viewModel.duration),
    statusLabels[viewModel.status] || "Running",
  ].filter(Boolean).join(" · ");

  return (
    <section className={`execution-trace-panel execution-trace-panel--${viewModel.status}`} aria-label="Execution progress">
      <button type="button" className="execution-trace-summary" aria-expanded={!collapsed} onClick={() => {
        manuallyToggled.current = true;
        setCollapsed((value) => !value);
      }}>
        <span className="execution-trace-chevron" aria-hidden="true">{collapsed ? <ChevronRight /> : <ChevronDown />}</span>
        <span className="execution-trace-header-copy">
          <span className="execution-trace-summary-text">{viewModel.title}</span>
          <span className="execution-trace-summary-meta">{metadata}</span>
        </span>
      </button>
      {!collapsed ? (
        <div className="execution-trace-content">
          <div className="execution-trace-steps">{viewModel.steps.map((step) => <Step key={step.id} step={step} />)}</div>
          {viewModel.artifacts.length ? <div className="execution-trace-orphan-artifacts">{viewModel.artifacts.map((artifact) => <Artifact key={artifact.id} artifact={artifact} />)}</div> : null}
        </div>
      ) : null}
    </section>
  );
};

export default React.memo(ExecutionTraceView);
